const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places';
/** Global Google calls once the API (incl. `libraries=places`) has fully initialized. */
const GOOGLE_MAPS_READY_CALLBACK = '__osGoogleMapsReady';

declare global {
  interface Window {
    [GOOGLE_MAPS_READY_CALLBACK]?: () => void;
  }
}

let loadPromise: Promise<void> | null = null;

export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  // Issue #1345: with `&loading=async`, `script.onload` fires before the API (and the
  // `places` library) finishes initializing. Google's `callback=` fires exactly when it has —
  // no polling. A capped poll used to start at the CALL, not the script load, so a second
  // caller on a slow page (map preview next to the autocomplete) timed out → "Failed to load
  // Google Maps", permanently.
  if (window.google?.maps?.places?.AutocompleteService) return Promise.resolve();

  loadPromise ??= new Promise<void>((resolve, reject) => {
    // Set on window (not module state) so a tag injected by an earlier module instance
    // (Vite HMR) still resolves this promise.
    window[GOOGLE_MAPS_READY_CALLBACK] = () => resolve();
    let script = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=${GOOGLE_MAPS_READY_CALLBACK}`;
      script.async = true;
      document.head.appendChild(script);
    }
    const tag = script;
    tag.addEventListener(
      'error',
      () => {
        // Drop the dead tag + promise so the next caller retries.
        tag.remove();
        loadPromise = null;
        reject(new Error('Failed to load Google Maps'));
      },
      { once: true },
    );
  }).then(() => google.maps.importLibrary('places').then(() => undefined));
  return loadPromise;
}

/**
 * Load Google Maps for the ScreenMap component.
 * Ensures the Map constructor is available (not just the bootstrap stub).
 */
export function loadGoogleMapsWithMarker(apiKey: string): Promise<void> {
  if (window.google?.maps?.Map) return Promise.resolve();

  // With `loading=async` only the requested libraries (`places`) load eagerly; `Map` lands
  // whenever the `maps` library happens to follow, so import it explicitly.
  return loadGoogleMapsScript(apiKey)
    .then(() => google.maps.importLibrary('maps'))
    .then(() => undefined);
}

/**
 * Preload the Google Maps script (call on hover/mount to reduce perceived latency).
 * Non-blocking — doesn't return a promise.
 */
export function preloadGoogleMapsScript(apiKey: string): void {
  if (!apiKey) return;
  loadGoogleMapsScript(apiKey).catch(() => undefined);
}
